/** Max evidence chars sent to soft pin-case classify (matches server OBJECT_TEXT_CAP). */
export const OBJECT_TEXT_CAP = 600;
export const DONE_FILL_EVIDENCE_MAX = OBJECT_TEXT_CAP;

export function truncateEvidenceText(text) {
  return String(text || '').trim().slice(0, DONE_FILL_EVIDENCE_MAX);
}

export function needsIntentSummary(text) {
  return String(text || '').trim().length > OBJECT_TEXT_CAP;
}

/**
 * Short body → classify the actual words (raw / OCR).
 * Long body (incl. OCR) → classify the distilled intentSummary when present.
 */
export function resolveDoneFillText({
  kind = 'text',
  rawText = '',
  ocrText = '',
  intentSummary = '',
} = {}) {
  const summary = String(intentSummary || '').trim();
  const image = String(kind || '').trim().toLowerCase() === 'image';
  const body = String(image ? ocrText : rawText || '').trim();
  if (needsIntentSummary(body) && summary) {
    return truncateEvidenceText(summary);
  }
  return truncateEvidenceText(body) || truncateEvidenceText(summary);
}

/** Sources for canvas evidence. */
export const CANVAS_EVIDENCE_SOURCE = {
  CLIPBOARD_PASTE: 'clipboard-paste',
  TLDRAW_TEXT_TOOL: 'tldraw-text-tool',
  TLDRAW_IMAGE_UPLOAD: 'tldraw-image-upload',
};

export function isTypedCanvasTextShape(shape) {
  if (!shape || (shape.type !== 'text' && shape.type !== 'note')) return false;
  if (shape.meta?.source === CANVAS_EVIDENCE_SOURCE.CLIPBOARD_PASTE) return false;
  if (shape.meta?.clipboardPaste) return false;
  return true;
}

/**
 * Pasted or typed canvas evidence that participates in digest / doneFills.
 * Pin-cards have their own cascade delete path.
 */
export function isCanvasEvidenceShape(shape) {
  if (!shape || shape.typeName === 'binding') return false;
  const type = String(shape.type || '');
  if (
    type === 'pin-card'
    || type === 'suggest-pin'
    || type === 'frame'
    || type === 'arrow'
  ) {
    return false;
  }
  const meta = shape.meta && typeof shape.meta === 'object' ? shape.meta : {};
  if (meta.source === CANVAS_EVIDENCE_SOURCE.CLIPBOARD_PASTE || meta.clipboardPaste) {
    return true;
  }
  if (String(meta.intentSummary || '').trim()) return true;
  if (String(meta.ocrText || '').trim()) return true;
  if (String(meta.doneKey || '').trim() || String(meta.partialKey || '').trim()) {
    return true;
  }
  return false;
}

/**
 * Build soft pin-case evidence for a pasted/typed shape.
 * Text: prefer enrich intentSummary when the dump is over OBJECT_TEXT_CAP.
 * Image: prefer OCR when short; intentSummary when OCR is over cap.
 */
export function buildPasteCanvasEvidence({
  shapeId,
  kind,
  rawText = '',
  intentSummary = '',
  ocrText = '',
  source = CANVAS_EVIDENCE_SOURCE.CLIPBOARD_PASTE,
  aiEnriched = false,
} = {}) {
  const id = String(shapeId || '').trim();
  const kindNorm = String(kind || '').trim().toLowerCase() === 'image' ? 'image' : 'text';
  const summary = String(intentSummary || '').trim();
  const text = resolveDoneFillText({
    kind: kindNorm,
    rawText,
    ocrText,
    intentSummary: summary,
  });
  if (!id || !text) return null;
  return {
    shapeId: id,
    kind: kindNorm,
    text,
    intentSummary: summary,
    source: String(source || CANVAS_EVIDENCE_SOURCE.CLIPBOARD_PASTE),
    aiEnriched: Boolean(aiEnriched),
  };
}

/**
 * True when this text/note already went through digest or pin-case.
 * Hydrate / reparent can re-emit `added`; those must not re-classify.
 * A real text edit still re-runs (caller passes skipIfClassified: false).
 */
export function shapeAlreadyDigested(shape) {
  const meta = shape?.meta && typeof shape.meta === 'object' ? shape.meta : {};
  return Boolean(
    String(meta.doneKey || '').trim()
    || String(meta.partialKey || '').trim()
    || String(meta.intentSummary || '').trim()
  );
}

/**
 * User-placed tldraw images that should run the clipboard-image enrich/digest path.
 * Skips AI-generated images, clipboard paste (already handled), and prior attempts.
 */
export function shouldDigestImageShape(shape) {
  if (!shape || shape.type !== 'image') return false;
  const meta = shape.meta && typeof shape.meta === 'object' ? shape.meta : {};
  if (meta.aiGenerated) return false;
  if (meta.clipboardPaste) return false;
  if (meta.source === CANVAS_EVIDENCE_SOURCE.CLIPBOARD_PASTE) return false;
  if (meta.source === CANVAS_EVIDENCE_SOURCE.TLDRAW_IMAGE_UPLOAD) return false;
  if (meta.imageDigestAttempted) return false;
  if (shapeAlreadyDigested(shape)) return false;
  return true;
}

/**
 * Only user-pin activity fills get placeDonePinInGapFrame.
 * Pasted image/text evidence keeps the user paste location (no frame).
 */
export function shouldFrameDoneFill({
  matchedActivityId = null,
  matchedShapeId = null,
  matchedKey = '',
} = {}) {
  const aid = String(matchedActivityId || '').trim();
  const shapeId = String(matchedShapeId || '').trim();
  const key = String(matchedKey || '').trim();
  return Boolean(aid && !shapeId && key);
}
